import React from 'react';
import { withStyles } from '@material-ui/core';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import { useCalculatePurchaseOrderDetails, useRetrievePurchaseOrderDetails, useRetrievePurchaseOrdersSummary, useSetPurchaseOrderDetailsQueryData, useUpdatePurchaseOrderDetails } from 'api/purchaseOrders/purchaseOrders';
import { useSnackBar } from 'components/common/SnackBars/SnackBar.hooks';
import FormView from 'components/FormView';
import styles from './OrderDetails.styles';
import OrderDetailFrom from './OrderDetails.form';
import { IOrderDetailsHandle, IOrderDetailsProps, setActionBarFn } from './OrderDetails.properties';
import { ActionBarIcons, changeConfirmationDialogDefaults } from './OrderDetails.constants';
import { usePOContextSelector, usePODispatch } from '../PurchaseOrders.context';
import { objectifyAddressForPost } from 'api/utils';
import { PurchaseOrderDetailsCalculationFields } from '../PurchaseOrders.constants';
import { PurchaseOrdersActionTypes } from '../PurchaseOrders.actions';

const setActionBarButtons = (setActionBarCallBack: setActionBarFn): void => { setActionBarCallBack(ActionBarIcons); };

const OrderDetails = (props: IOrderDetailsProps, ref: React.Ref<IOrderDetailsHandle>): JSX.Element => {
  const { classes, changeConfirmationDialog, resetForm, dirty, formValues, destroyForm } = props;
  const { setActionBar } = React.useContext(ActionBarContext);

  const params = new URLSearchParams(location.search);
  const PurchaseOrderId = usePOContextSelector<'PurchaseOrder'>((state) => state.PurchaseOrder) || params.get('PurchaseOrder');
  const { data: PurchaseOrderDetails, isLoading: isPurchaseOrderDetailsLoading, isFetching: isPurchaseOrderDetailsFetching } = useRetrievePurchaseOrderDetails(PurchaseOrderId);
  const calculateOrderDetailsMutation = useCalculatePurchaseOrderDetails();
  const updateOrderDetailsMutation = useUpdatePurchaseOrderDetails();
  const setPurchaseOrderDetailsQueryData = useSetPurchaseOrderDetailsQueryData();
  const { showSnackBar } = useSnackBar();
  const onCancelClicked = React.useRef(false);
  const contextDispatch = usePODispatch();

  const fetchPOSummary = useRetrievePurchaseOrdersSummary(usePOContextSelector<'PurchaseOrder'>((state) => state.PurchaseOrder));

  const [internalDirty, _setInternalDirty] = React.useState<boolean>(false);
  const isDirty = internalDirty || dirty;

  const resetAndNavigateBack = (callBack: () => void) => {
    resetForm();
    destroyForm();
    callBack();
  };

  React.useImperativeHandle(
    ref,
    () => ({
      isChangesSavedOrDiscarded(): boolean {
        if (isDirty) showSnackBar({ variant: 'warning', message: 'Please save or discard changes.' });

        return !isDirty;
      },
      async OrderDetailsOk(navigateBack: () => void): Promise<void> {
        if (await updatePurchaseOrderDetails()) {
          resetAndNavigateBack(navigateBack);
        }
      },
      OrderDetailsCancel(navigateBack: () => void): void {
        onCancelClicked.current = true;
        if (!isDirty) return resetAndNavigateBack(navigateBack);

        changeConfirmationDialog({
          ...changeConfirmationDialogDefaults,
          onOk: () => {
            resetAndNavigateBack(navigateBack);
          },
          onCancel: () => {
            onCancelClicked.current = false;
          }
        });
      }
    }),
    [formValues, isDirty]
  );

  React.useEffect(
    () => { setActionBarButtons(setActionBar); },
    []
  );

  React.useEffect(
    () => {
      if (isDirty) {
        contextDispatch({
          [PurchaseOrdersActionTypes.setPreventChangeStatus]: true,
        });
      } else {
        contextDispatch({
          [PurchaseOrdersActionTypes.setPreventChangeStatus]: false,
        });
      }
    },
    [isDirty]
  );

  React.useEffect(
    () => {
      resetForm();
    },
    [PurchaseOrderId]
  );

  const updatePurchaseOrderDetails = React.useCallback(
    async () => {
      const response = await updateOrderDetailsMutation.mutateAsync({
        PurchaseOrder: PurchaseOrderId,
        PurchaseOrderDetails: {
          ...formValues,
          UserId: formValues.ResponsiblePerson,
          ...objectifyAddressForPost((formValues as any)?.address, 'SupplierAddress', ['Addressee', 'Line1', 'Line2', 'City', 'State', 'PostalCode', 'Country']),
          address: undefined,
        }
      });

      if (response?.Status && response?.PurchaseOrderDetails) {
        setPurchaseOrderDetailsQueryData(PurchaseOrderId, response);
        _setInternalDirty(false);
        fetchPOSummary.refetch();
      }

      return response?.Status;
    },
    [formValues, PurchaseOrderId]
  );

  const calculatePurchaseOrderDetail = React.useCallback(
    async (fieldName, value) => {
      if (value && PurchaseOrderDetailsCalculationFields.includes(fieldName) &&
        PurchaseOrderDetails?.PurchaseOrderDetails?.inlineObject[fieldName] !== value) {

        const response = await calculateOrderDetailsMutation.mutateAsync({
          PurchaseOrder: PurchaseOrderId,
          PurchaseOrderDetails: {
            ...formValues,
            UserId: formValues.ResponsiblePerson,
            ...objectifyAddressForPost((formValues as any)?.address, 'SupplierAddress', ['Addressee', 'Line1', 'Line2', 'City', 'State', 'PostalCode', 'Country']),
            address: undefined,
            [fieldName]: value,
          },
          urlQueryParams: {
            ChangedField: fieldName
          }
        });

        if (response?.Status && response?.PurchaseOrderDetails) {
          setPurchaseOrderDetailsQueryData(
            PurchaseOrderId,
            {
              ...response,
              PurchaseOrderDetails: {
                ...response?.PurchaseOrderDetails,
                inlineObject: {
                  ...response?.PurchaseOrderDetails?.inlineObject,
                  ResponsiblePerson: response?.PurchaseOrderDetails?.inlineObject.UserId
                }
              }
            }
          );
          _setInternalDirty(true);
        }
      }
    },
    [PurchaseOrderId, PurchaseOrderDetails, formValues]
  );

  const onLookupSelectedItemChange = React.useCallback(
    (name, item) => {
      calculatePurchaseOrderDetail(name, item?.Code);
    },
    [calculatePurchaseOrderDetail]
  );

  const handleDateOnChange = React.useCallback(
    (name, value) => {
      if (!formValues) {  // it means the form is not loaded yet
        return;
      }

      let date = '';
      Object.keys(value).forEach((k: string) => {
        if (/^[0-9]*$/.test(k)) {
          date += value[k];
        }
      });
      calculatePurchaseOrderDetail(name, date);
    },
    [formValues, calculatePurchaseOrderDetail]
  );

  return (
    <div className={classes.formContainer}>
      <FormView
        isLoading={isPurchaseOrderDetailsLoading || isPurchaseOrderDetailsFetching}
        formName={'PurchaseOrdersOrderDetails'}
        schema={OrderDetailFrom}
        includeTabsHeight={true}
        initialValues={PurchaseOrderDetails?.PurchaseOrderDetails?.inlineObject}
        valuesSchema={PurchaseOrderDetails?.PurchaseOrderDetails?.schema}
        operationMode={'EDIT'}
        // onBlur={formFieldOnBlur}
        fieldExtensions={{
          Supplier: {
            onSelectedItemChange: (item) => onLookupSelectedItemChange('Supplier', item)
          },
          OrderType: {
            onSelectedItemChange: (item) => onLookupSelectedItemChange('OrderType', item)
          },
          PurchaseOrderCategory: {
            onSelectedItemChange: (item) => onLookupSelectedItemChange('PurchaseOrderCategory', item)
          },
          EarliestDueDate: {
            onChange: (v) => handleDateOnChange('EarliestDueDate', v)
          }
        }}
      />
    </div>
  );
};

export default withStyles(styles, { index: 1 })(React.memo(React.forwardRef(OrderDetails)));
